import type { SkillConfig } from "@/generated";
import type { CodeEditorLanguage } from "@/components/Form/CodeEditor/CodeEditor";
import { SKILL_MD } from "./skillMd";

export function getFileContent(skill: SkillConfig, fileName: string): string {
  if (fileName === SKILL_MD) return skill.instructions ?? "";
  return skill.resources?.[fileName] ?? "";
}

export function setFileContent(
  skill: SkillConfig,
  fileName: string,
  content: string
): SkillConfig {
  if (fileName === SKILL_MD) return { ...skill, instructions: content };
  return {
    ...skill,
    resources: { ...(skill.resources ?? {}), [fileName]: content },
  };
}

export function listFiles(skill: SkillConfig): string[] {
  return [SKILL_MD, ...Object.keys(skill.resources ?? {}).sort()];
}

export function validateFileName(
  name: string,
  skill: SkillConfig
): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Enter a file name.";
  if (trimmed === SKILL_MD || skill.resources?.[trimmed] !== undefined) {
    return "That file already exists.";
  }
  if (
    trimmed.startsWith("/") ||
    trimmed.split("/").some((segment) => segment === ".." || segment === ".")
  ) {
    return "Invalid file name.";
  }
  return null;
}

export function addFile(skill: SkillConfig, name: string): SkillConfig {
  return setFileContent(skill, name.trim(), "");
}

export function deleteFile(skill: SkillConfig, name: string): SkillConfig {
  if (name === SKILL_MD) return skill;
  const resources = { ...(skill.resources ?? {}) };
  delete resources[name];
  return { ...skill, resources };
}

// `originalName` is the name the skill already had. The previous UI validated
// nothing, so existing agents hold skills with empty or duplicate names —
// rejecting those would make a skill an agent already has uneditable until it
// is renamed. Only a problem the user has just introduced is reported.
export function renameFile(
  skill: SkillConfig,
  from: string,
  to: string
): SkillConfig {
  if (from === SKILL_MD) return skill;
  const resources = { ...(skill.resources ?? {}) };
  if (resources[from] === undefined) return skill;
  const content = resources[from];
  delete resources[from];
  return { ...skill, resources: { ...resources, [to.trim()]: content } };
}

// The name cap is the editor's alone: `SkillConfig` deliberately does not cap
// it, because a name is the skill's identifier and two legacy names sharing a
// 64-character prefix would clip to the same string. A name already stored is
// accepted whatever its length (see `validateSkill`); only one being written
// here is held to this.
export const NAME_MAX = 64;

// Mirrors SKILL_USAGE_CONDITIONS_MAX, which clips anything longer on the way
// in — a value past it would come back from the backend trimmed, so the editor
// refuses it rather than silently losing the tail.
export const USAGE_CONDITIONS_MAX = 300;

// The backend cap counts Python characters, which are code points. A string's
// `.length` counts UTF-16 units, so an emoji costs two and the editor would
// refuse text the backend accepts whole.
const countCharacters = (text: string): number => [...text].length;

export function validateSkill(
  skill: SkillConfig,
  otherNames: string[],
  originalName?: string
): string | null {
  const name = skill.name.trim();
  if (originalName !== undefined && name === originalName.trim()) return null;

  if (!name) return "Enter a name.";
  if (countCharacters(name) > NAME_MAX) {
    return `Cannot be longer than ${NAME_MAX} characters.`;
  }
  const taken = otherNames.some(
    (other) => other.trim().toLowerCase() === name.toLowerCase()
  );
  return taken ? "A skill with that name already exists." : null;
}

// Required here but optional on SkillConfig: skills predate the requirement,
// so an agent holding one written without usage conditions still loads — it
// just cannot be saved again until they are filled in.
export function usageConditionsError(skill: SkillConfig): string | null {
  const text = (skill.usage_conditions ?? "").trim();
  if (!text) {
    return "Enter usage conditions. The agent uses them to decide when to load this skill.";
  }
  return countCharacters(text) > USAGE_CONDITIONS_MAX
    ? `Cannot be longer than ${USAGE_CONDITIONS_MAX} characters.`
    : null;
}

const LANGUAGE_BY_EXTENSION: Record<string, CodeEditorLanguage> = {
  py: "python",
  json: "json",
  sql: "sql",
  xml: "xml",
};

// Markdown has no CodeMirror mode here, and "text" is the point: a skill body
// must come back out of the editor exactly as it went in.
export function fileLanguage(fileName: string): CodeEditorLanguage {
  const extension = fileName.split(".").pop()?.toLowerCase() ?? "";
  return LANGUAGE_BY_EXTENSION[extension] ?? "text";
}

// A skill is a markdown package, so prose is the default: only the types the
// code editor actually has a mode for opt out. A file named without an
// extension, or with an unknown one, is treated as markdown.
export function isMarkdownFile(fileName: string): boolean {
  return fileLanguage(fileName) === "text";
}
